import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StateBlock } from '../components';
import { getCampaignName, useCampaign, useCampaignApproval, useMyApprovals } from '@/api/recognition/hooks';
import { getRecognitionErrorMessage } from '@/api/recognition/client';
import type { CampaignApprovalDto } from '@/api/recognition/types';

/**
 * WP-B5 (RN-C11) — «Mis Solicitudes de Campaña».
 *
 * La contraparte de la bandeja del Admin Global: «Solicitante: estado (Pendiente/Aprobada/Rechazada)
 * con comentario de rechazo + Editar/Borrar mientras esté pendiente». Existía la bandeja del que
 * aprueba y nada del que pide.
 *
 * Columnas y acciones son las de la maqueta, medidas una a una: Campaña · Fecha solicitud · Estado ·
 * Comentario · Acciones, y las acciones dependen del estado — pendiente: editar + retirar;
 * aprobada: ninguna; rechazada: volver a enviar.
 */

export type AccionSolicitud = 'editar' | 'retirar' | 'reenviar';

/**
 * Qué puede hacer el solicitante con una solicitud, según su estado (maqueta: `fa-pen`+`fa-times`
 * en Pendiente, nada en Aprobada, `fa-redo` en Rechazada).
 *
 * 🔴 «Aprobada» sin acciones no es un olvido de la maqueta: editar una campaña ya aprobada
 * reabriría el flujo por la puerta de atrás, y eso RN-C11 lo acota aparte («un Admin OU sin campaña
 * propia activa no edita campañas aprobadas: solo exclusiones y propuestas»).
 */
export function accionesDe(estado: string): AccionSolicitud[] {
  switch (estado) {
    case 'pendiente': return ['editar', 'retirar'];
    case 'rechazada': return ['reenviar'];
    default: return [];
  }
}

/** Clase del distintivo de estado — mismos tres que el resto de la administración. */
export function badgeDeEstado(estado: string): string {
  if (estado === 'aprobada') return 'adm-badge adm-badge--ok';
  if (estado === 'rechazada') return 'adm-badge adm-badge--danger';
  return 'adm-badge adm-badge--warn';
}

// Mismo patrón que `CampaignNameCell` en AdminCampaigns.tsx (la bandeja del que aprueba): la
// columna «Campaña» mostraba `campaignId` crudo, porque `CampaignApprovalDto` no trae el nombre
// —solo el id—. Se resuelve en el idioma actual y degrada al id si la campaña no cargó todavía.
function CampaignNameCell({ campaignId }: Readonly<{ campaignId: string }>) {
  const { i18n } = useTranslation('recognition');
  const { data } = useCampaign(campaignId);
  const lang = i18n.language.split('-')[0];
  return <>{(data && getCampaignName(data, lang)) || campaignId}</>;
}

export function MisSolicitudesCampana() {
  const { t } = useTranslation('recognition');
  const navigate = useNavigate();
  const solicitudes = useMyApprovals();
  const approvalM = useCampaignApproval();

  const list: CampaignApprovalDto[] = solicitudes.data ?? [];

  const actuar = (s: CampaignApprovalDto, accion: AccionSolicitud) => {
    // ⚠️ El panel navega por SLUG (`/app/admin/<slug>`), y `AdminCampaigns` no acepta enlace profundo
    // a una campaña concreta: lleva su selección en estado interno. Así que «Editar» abre el admin de
    // Campañas, no la campaña. Es la MISMA limitación que ya se decidió en P18 y por la misma razón
    // —construir el enlace profundo es trabajo aparte—; se deja anotada en vez de fingir que enlaza.
    if (accion === 'editar') { navigate('/app/admin/campanas'); return; }
    approvalM.mutate({ id: s.campaignId, action: accion === 'retirar' ? 'cancel' : 'submit' });
  };

  return (
    <section>
      <h4 className="adm-sub-title"><i className="fas fa-paper-plane" /> {t('admin.misSolicitudes.title')}</h4>
      <p className="adm-note">{t('admin.misSolicitudes.desc')}</p>

      {approvalM.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(approvalM.error, t('admin.saveError'))}
        </p>
      )}

      <StateBlock
        isLoading={solicitudes.isLoading}
        isError={solicitudes.isError}
        error={solicitudes.error}
        isEmpty={list.length === 0}
        emptyText={t('admin.misSolicitudes.empty')}
        onRetry={() => { solicitudes.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.misSolicitudes.colCampana')}</th>
                <th>{t('admin.misSolicitudes.colFecha')}</th>
                <th>{t('admin.misSolicitudes.colEstado')}</th>
                <th>{t('admin.misSolicitudes.colComentario')}</th>
                <th>{t('admin.misSolicitudes.colAcciones')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const acciones = accionesDe(s.estado);
                return (
                  <tr key={s.id}>
                    <td><CampaignNameCell campaignId={s.campaignId} /></td>
                    <td>{new Date(s.solicitadoAt).toLocaleDateString()}</td>
                    <td><span className={badgeDeEstado(s.estado)}>{t(`admin.misSolicitudes.estado.${s.estado}`, s.estado)}</span></td>
                    {/* El comentario es el MOTIVO del rechazo, la razón principal de esta pantalla.
                        Sin comentario se pone un guion: un hueco, no una cadena vacía que parezca un fallo. */}
                    <td>{s.comentario?.trim() || '—'}</td>
                    <td>
                      {acciones.length === 0 && <span>—</span>}
                      {acciones.includes('editar') && (
                        <button type="button" className="btn btn--ghost" title={t('admin.misSolicitudes.editar')}
                          aria-label={t('admin.misSolicitudes.editar')} onClick={() => actuar(s, 'editar')}>
                          <i className="fas fa-pen" />
                        </button>
                      )}
                      {acciones.includes('retirar') && (
                        <button type="button" className="btn btn--ghost" title={t('admin.misSolicitudes.retirar')}
                          aria-label={t('admin.misSolicitudes.retirar')} disabled={approvalM.isPending}
                          onClick={() => actuar(s, 'retirar')}>
                          <i className="fas fa-times" />
                        </button>
                      )}
                      {acciones.includes('reenviar') && (
                        <button type="button" className="btn btn--ghost" title={t('admin.misSolicitudes.reenviar')}
                          aria-label={t('admin.misSolicitudes.reenviar')} disabled={approvalM.isPending}
                          onClick={() => actuar(s, 'reenviar')}>
                          <i className="fas fa-redo" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </StateBlock>
    </section>
  );
}
